import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/lib/seo';
import { countOf } from '@/lib/trial';
import { LANDING_FAQ } from '@/modules/marketing/landing-faq';
import { MarketingPage } from '@/modules/marketing/MarketingPage';
import { TRIAL } from '@/modules/marketing/pricing-data';
import { PricingTable } from '@/modules/marketing/PricingTable';
import { PAGES } from '@/seo/pages';

/**
 * Pricing, at its own address.
 *
 * ## Why this is not just the section on the home page
 *
 * It was, and `#pricing` is not a page. It cannot appear in a search result, cannot be given its
 * own description, cannot be linked from an invoice or a support reply, and does not exist at all
 * to anything that reads a URL rather than scrolls. "<product> pricing" is one of the two or three
 * highest-intent searches anybody makes about a subscription product, and there was nothing for it
 * to find.
 *
 * The home page keeps its pricing section. Both render the same {@link PricingTable}, so there is
 * one place a price is written and no way for the two to disagree.
 *
 * ## The questions are here rather than only on the home page
 *
 * Somebody on a pricing page is deciding, and the things they are about to wonder — is there a
 * trial, what happens when it ends, what happens if I cancel, do you sell the servers — are the
 * questions already written. Repeating them here is not padding; it is answering them at the moment
 * they are asked.
 */
export function PricingPage() {
  usePageMeta(PAGES.pricing);

  return (
    <MarketingPage
      eyebrow="Pricing"
      title="Automation, monitoring and backups on every plan"
      standfirst="Plans differ by how many servers, monitors and team members they include. Pay yearly and get two months free."
      wide
    >
      <PricingTable />

      <div className="mx-auto mt-20 max-w-3xl">
        <h2 className="text-center text-2xl font-semibold tracking-tight">Frequently asked questions</h2>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {LANDING_FAQ.map((item) => (
            <div key={item.q} className="border-border/60 bg-muted/10 rounded-xl border p-6">
              <h3 className="font-medium">{item.q}</h3>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="border-border/60 from-primary/10 mx-auto mt-20 max-w-4xl rounded-2xl border bg-gradient-to-b to-transparent px-6 py-12 text-center">
        <h2 className="text-2xl font-semibold tracking-tight text-balance">
          Ready to connect your servers?
        </h2>
        <p className="text-muted-foreground mx-auto mt-3 max-w-xl leading-relaxed">
          Start with a {TRIAL.days}-day free trial on {countOf(TRIAL.servers, 'server', 'servers')}, with no
          card. There is no agent to install, and your servers keep running whatever you decide.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/register"
            className="bg-primary text-primary-foreground inline-flex h-11 items-center justify-center gap-2 rounded-lg px-6 text-[15px] font-medium transition-opacity hover:opacity-90"
          >
            Start free trial
            <ArrowRight className="size-4" aria-hidden />
          </Link>
          <Link
            to="/docs"
            className="border-border/60 hover:bg-muted/50 inline-flex h-11 items-center justify-center gap-2 rounded-lg border px-6 text-[15px] font-medium transition-colors"
          >
            Read the documentation
          </Link>
        </div>
      </div>
    </MarketingPage>
  );
}
